import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { Camera } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import PreviousScans from '../PreviousScans';
import { Button } from 'components/ui/button';
import VitalSignDetail from './VitalSignDetail';
import { VoiceScanReport } from 'services/voice-scan/type';
import { useGetVoiceReportDetail } from 'services/voice-scan/hooks';
import { VoiceReadingData } from 'types/voice';

interface MobileScanReportProps {
  readings: VoiceScanReport[];
  setReadings: Dispatch<SetStateAction<VoiceScanReport[]>>;
  onScan: () => void;
  refetchScanReports?: () => void;
  scrollViewElement: React.ReactNode;
}

export default function MobileScanReport({
  readings,
  setReadings,
  onScan,
  refetchScanReports,
  scrollViewElement,
}: MobileScanReportProps) {
  const { t } = useTranslation();

  const [searchParams] = useSearchParams();
  const scanId = searchParams.get('scan_id');

  const [selectedReadingId, setSelectedReadingId] = useState<string>('');

  const { data: voiceReportDetail } = useGetVoiceReportDetail({
    sessionId: selectedReadingId,
  });
  const categorizedVoiceData = useMemo(() => {
    if (!voiceReportDetail) {
      return null;
    }

    const data: Record<string, VoiceReadingData[]> = {};
    Object.entries(voiceReportDetail?.sub_categorization).forEach(
      ([key, categoryKeys]) => {
        const categoryValues: VoiceReadingData[] = [];
        categoryKeys.forEach((categoryKey) => {
          const configData = voiceReportDetail.scale_config?.[categoryKey];
          const scanData = voiceReportDetail?.voice_scan_report?.find(
            (report) => report.key === categoryKey
          );

          if (configData && scanData) {
            categoryValues.push({
              display: configData.display,
              key: scanData?.key,
              long_intro: configData.long_intro,
              colorRange: configData.color_range ?? [],
              short_intro: configData.short_intro,
              category: scanData?.category ?? '',
              color: scanData?.color,
              value: scanData?.value ?? 0,
            });
          }
        });
        data[key] = categoryValues;
      }
    );

    return data;
  }, [voiceReportDetail]);
  const scanData = useMemo(() => {
    if (!categorizedVoiceData || !Object.keys(categorizedVoiceData)?.length) {
      return [];
    }

    return Object.values(categorizedVoiceData).flat();
  }, [categorizedVoiceData]);

  const onDelete = () => {
    refetchScanReports?.();
    setSelectedReadingId('');
  };

  useEffect(() => {
    if (scanId && scanData && scanData.length) {
      setSelectedReadingId(scanId);
    }
  }, [scanId, scanData]);

  if (selectedReadingId) {
    return (
      <VitalSignDetail
        data={categorizedVoiceData}
        scanData={scanData}
        setSelectedReadingId={setSelectedReadingId}
        readingId={selectedReadingId}
        refetchScanReports={onDelete}
        voiceReportDetail={voiceReportDetail}
      />
    );
  }

  return (
    <div className='relative block p-4 sm:hidden'>
      <div className='flex justify-end'>
        <Button
          className='h-8 text-sm rounded-full bg-rose-400 hover:bg-rose-400/90'
          size='sm'
          onClick={onScan}
        >
          <Camera className='w-4 h-4 mr-2' />
          {t('start_scan')}
        </Button>
      </div>

      <div className='mt-4'>
        <PreviousScans
          readings={readings}
          setReadings={setReadings}
          selectedReadingId={selectedReadingId}
          setSelectedReadingId={setSelectedReadingId}
          scrollViewElement={scrollViewElement}
        />
      </div>
    </div>
  );
}
